import React from 'react';
import { useTheme } from '../../context/ThemeContext';

function Bone({ className = '' }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-line dark:bg-line-dark ${className}`}
      aria-hidden
    />
  );
}

/**
 * Layout-matched loading stand-in for the admin roster.
 * Shape follows header → KPI strip → filter bar → table (desktop) / cards (mobile).
 */
export default function AdminDashboardSkeleton({ direction = 'ltr' }) {
  const { themeClasses } = useTheme();
  const card = `${themeClasses.bgCard} rounded-lg border border-line dark:border-line-dark`;

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-120 pb-8 font-ui`}
      dir={direction}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={direction === 'rtl' ? 'טוען את לוח הבקרה' : 'Loading dashboard'}
    >
      <span className="sr-only">
        {direction === 'rtl' ? 'טוען לקוחות וסטטיסטיקות' : 'Loading clients and statistics'}
      </span>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header: referral + Refresh (no page title) */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <Bone className="h-4 w-40" />
          <Bone className="h-11 w-28" />
        </div>

        {/* KPI strip — 4 tiles, same grid as AdminKpiStrip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className={`${card} min-h-[5.75rem] px-3 py-3 flex flex-col justify-between gap-3`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Bone className="h-8 w-8 shrink-0" />
                  <div className="space-y-1.5">
                    <Bone className="h-3 w-20" />
                    <Bone className="h-2.5 w-14" />
                  </div>
                </div>
                <Bone className="h-8 w-8 rounded-full" />
              </div>
              <div className="flex items-end justify-between gap-3">
                <Bone className="h-7 w-16" />
                <Bone className="h-[22px] w-[72px]" />
              </div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className={`${card} p-4 sm:p-5 mb-6`}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
            <Bone className="h-11 flex-1" />
            <Bone className="h-11 w-full sm:w-28" />
            <Bone className="h-11 w-full sm:w-24" />
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3 mb-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`${card} p-4`}>
              <div className="flex items-center gap-3 mb-3">
                <Bone className="h-10 w-10 rounded-full shrink-0" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Bone className="h-4 w-32" />
                  <Bone className="h-3 w-24" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Bone className="h-8" />
                <Bone className="h-8" />
                <Bone className="h-8" />
                <Bone className="h-8" />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className={`hidden md:block ${card} overflow-hidden`}>
          <div className="border-b border-line dark:border-line-dark px-3 py-2.5 flex gap-3">
            <Bone className="h-3 w-20" />
            <Bone className="h-3 w-24" />
            <Bone className="h-3 w-16" />
            <Bone className="h-3 w-28" />
            <Bone className="h-3 w-24" />
            <Bone className="h-3 w-32" />
            <Bone className="h-3 w-16" />
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2.5 border-b border-line dark:border-line-dark last:border-b-0"
            >
              <Bone className="h-10 w-10 rounded-full shrink-0" />
              <Bone className="h-4 w-28" />
              <Bone className="h-3 flex-1 max-w-[8rem]" />
              <Bone className="h-5 w-16 rounded-full" />
              <Bone className="h-3 w-20" />
              <Bone className="h-3 w-24" />
              <Bone className="h-3 w-16" />
              <Bone className="h-8 w-8 rounded-md shrink-0" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
